import { BooleanInput, useTranslate } from 'react-admin';
import { Box, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useFieldArray, useWatch } from 'react-hook-form';
import PropTypes from 'prop-types';
import { ParameterValueInput } from '@/components/parameter/fields/ParameterValueInput.jsx';
import { AddSubsetButton } from '@/components/parameter/buttons/AddSubsetButton.jsx';
import { RemoveSubsetButton } from '@/components/parameter/buttons/RemoveSubsetButton.jsx';
import { ParameterSubsetTypeInput } from '@/components/parameter/subsets/ParameterSubsetTypeInput.jsx';
import { ParameterSubsetTargetInput } from '@/components/parameter/subsets/ParameterSubsetTargetInput.jsx';
import { canAddChildSubset } from '@/constants/ParameterSubsetTypes.js';
import { createEmptySubset } from '@/helpers/parameter/parameterSubsets.js';
import { parameterSubsetActionsStyles, parameterSubsetGroupStyles, parameterSubsetHeaderStyles, parameterSubsetSwitchesStyles } from '@/themes/commonStyles';

const StyledSubsetGroup = styled(Box, { shouldForwardProp: prop => prop !== 'depth' })(({ depth }) => parameterSubsetGroupStyles(depth));

const StyledSubsetHeader = styled(Box)(parameterSubsetHeaderStyles);

const StyledSubsetActions = styled(Stack)(parameterSubsetActionsStyles);

const StyledSubsetSwitches = styled(Box)(parameterSubsetSwitchesStyles);

export const ParameterSubsetGroup = ({ source, parentType, depth, onRemove }) => {
    const translate = useTranslate();
    const type = useWatch({ name: `${source}.type` });
    const nestedSource = `${source}.subsets`;
    const { fields, append, remove } = useFieldArray({ name: nestedSource, keyName: 'fieldKey' });
    const showAddNested = Boolean(type) && canAddChildSubset(type);

    return (
        <StyledSubsetGroup depth={depth}>
            <StyledSubsetHeader>
                <Typography variant='h6'>{translate('resources.parameter.fields.subset')}</Typography>
            </StyledSubsetHeader>
            <ParameterSubsetTypeInput source={`${source}.type`} targetSource={`${source}.subset`} nestedSource={nestedSource} parentType={parentType} />
            <ParameterSubsetTargetInput source={`${source}.subset`} typeSource={`${source}.type`} />
            <ParameterValueInput source={`${source}.value`} isRequired={false} />
            <StyledSubsetSwitches>
                <BooleanInput source={`${source}.isActive`} label={translate('common.active')} defaultValue={true} size='small' />
                <BooleanInput
                    source={`${source}.isSignificant`}
                    label={translate('resources.parameter.fields.isSignificant')}
                    defaultValue={false}
                    size='small'
                />
                <BooleanInput
                    source={`${source}.deactivateOthers`}
                    label={translate('resources.parameter.fields.deactivateOthers')}
                    defaultValue={false}
                    size='small'
                />
            </StyledSubsetSwitches>
            <StyledSubsetActions direction='row'>
                {showAddNested ? <AddSubsetButton onAdd={() => append(createEmptySubset())} /> : null}
                <RemoveSubsetButton onRemove={onRemove} />
            </StyledSubsetActions>
            {fields.map((field, index) => (
                <ParameterSubsetGroup
                    key={field.fieldKey}
                    source={`${nestedSource}.${index}`}
                    parentType={type}
                    depth={depth + 1}
                    onRemove={() => remove(index)}
                />
            ))}
        </StyledSubsetGroup>
    );
};

ParameterSubsetGroup.propTypes = {
    source: PropTypes.string.isRequired,
    parentType: PropTypes.string,
    depth: PropTypes.number.isRequired,
    onRemove: PropTypes.func.isRequired,
};
